/**
 * Who is signed in, and what they may do.
 *
 * The permission list comes from `/auth/me`, which the backend generates from
 * **the same matrix it enforces with**. This file never encodes a rule of its
 * own — it only asks whether a string is in a set. That is what keeps a
 * button's visibility and a route's 403 from ever disagreeing.
 */

import { createContext, useCallback, useContext, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, type Me } from './api'

interface AuthValue {
  me: Me | null
  loading: boolean
  /** True when the signed-in role holds this permission. */
  can: (permission: string) => boolean
  /** True when the role holds every one of these. */
  canAll: (...permissions: string[]) => boolean
  /** True when the role holds at least one. */
  canAny: (...permissions: string[]) => boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  loginError: string | null
  loggingIn: boolean
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me')
      } catch (error) {
        // Not being signed in is an ordinary state, not a failure. Anything
        // else is a real problem and should surface.
        if (error instanceof ApiError && error.isUnauthenticated) return null
        throw error
      }
    },
    retry: false,
    staleTime: 30_000,
  })

  const loginMutation = useMutation({
    mutationFn: (vars: { email: string; password: string }) =>
      api.post<Me>('/auth/login', vars),
    onSuccess: (me) => queryClient.setQueryData(['me'], me),
  })

  const logoutMutation = useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      // Everything cached was fetched as the previous user, so it all goes.
      queryClient.clear()
      queryClient.setQueryData(['me'], null)
      // Then reload. Clearing the cache makes React Query immediately refetch
      // `/me`, and that request can race the browser's removal of the cookie
      // -- which lands the old session back in the cache and leaves the person
      // apparently still signed in. A reload has no in-memory state to race.
      // For a security action, certainty beats smoothness.
      window.location.assign('/')
    },
  })

  const permissions = useMemo(() => new Set(data?.permissions ?? []), [data])

  const can = useCallback((permission: string) => permissions.has(permission), [permissions])
  const canAll = useCallback(
    (...wanted: string[]) => wanted.every((p) => permissions.has(p)),
    [permissions],
  )
  const canAny = useCallback(
    (...wanted: string[]) => wanted.some((p) => permissions.has(p)),
    [permissions],
  )

  const value: AuthValue = {
    me: data ?? null,
    loading: isLoading,
    can,
    canAll,
    canAny,
    login: async (email, password) => {
      await loginMutation.mutateAsync({ email, password })
    },
    logout: async () => {
      await logoutMutation.mutateAsync()
    },
    loginError:
      loginMutation.error instanceof Error ? loginMutation.error.message : null,
    loggingIn: loginMutation.isPending,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}

/** Human-facing role names. The wire format stays snake_case. */
export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  finance_head: 'Finance Head',
  book_keeper: 'Book Keeper',
  employee: 'Employee',
}
